import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { LoanEligibilityCard } from "@/components/sacco-management/LoanEligibilityCard";
import { NetPositionCard } from "@/components/sacco-management/NetPositionCard";
import { DepositList } from "@/components/sacco-management/DepositList";
import { WithdrawalList } from "@/components/sacco-management/WithdrawalList";
import { ShareList } from "@/components/sacco-management/ShareList";
import { LoanForm } from "@/components/sacco-management/LoanForm";
import { LoanCard } from "@/components/sacco-management/LoanCard";
import { RepaymentSchedule } from "@/components/sacco-management/RepaymentSchedule";
import { RebateList } from "@/components/sacco-management/RebateList";
import { ContributionGoalList } from "@/components/sacco-management/ContributionGoalList";
import { CurrencyToggle } from "@/components/currency-toggle/CurrencyToggle";
import {
  useFetchSaccoLoanMembershipRepayments,
  useFetchSaccoMemberships,
  useFetchSaccoLoans,
  useSaccoMetrics,
  useFetchSaccoShares,
  useFetchSaccoWithdrawals,
  useFetchSaccoDeposits
} from "@saccoHooks";
import { useExchangeRates } from "@/hooks/useExchangeRates";

export default function SaccoDetailPage() {
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();

  const [loanFormOpen, setLoanFormOpen] = useState(false);
  const [expandedLoanId, setExpandedLoanId] = useState<string | null>(null);

  const { data: memberships = [], isLoading: membershipsLoading } = useFetchSaccoMemberships();
  const membership = memberships.find((m) => m.id === membershipId);

  const nativeCurrency = membership?.currency ?? 'KES';
  const [displayCurrency, setDisplayCurrency] = useState('KES');
  const { convert, isLoading: ratesLoading, error: ratesError } = useExchangeRates(nativeCurrency);

  // Sync displayCurrency to membership's native currency once membership loads
  useEffect(() => {
    if (membership?.currency) {
      setDisplayCurrency(membership.currency);
    }
  }, [membership?.currency]);

  const { data: deposits = [] } = useFetchSaccoDeposits(membershipId!);
  const { data: withdrawals = [] } = useFetchSaccoWithdrawals(membershipId!);
  const { data: shares = [] } = useFetchSaccoShares(membershipId!);
  const { data: loans = [] } = useFetchSaccoLoans(membershipId!);
  const { data: repayments = [] } = useFetchSaccoLoanMembershipRepayments(membershipId!);

  const metrics = useSaccoMetrics({ deposits, withdrawals, shares, repayments });
  const currentDepositBalance = metrics.totalDeposits - metrics.totalWithdrawals;

  if (membershipsLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-5xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!membership) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-5xl text-center py-20">
        <p className="text-muted-foreground mb-4">Membership not found.</p>
        <Button variant="outline" onClick={() => navigate("/investments/sacco")}>
          Back to Sacco
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/investments/sacco")}
          aria-label="Back to Sacco list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{membership.sacco_name}</h1>
          <p className="text-sm text-muted-foreground">{membership.membership_number}</p>
        </div>
        <CurrencyToggle
          baseCurrency={nativeCurrency}
          selectedCurrency={displayCurrency}
          onCurrencyChange={setDisplayCurrency}
          showBalance={false}
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deposits">Deposits</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="shares">Shares</TabsTrigger>
          <TabsTrigger value="loans">Loans</TabsTrigger>
          <TabsTrigger value="rebates">Rebates</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <NetPositionCard
              metrics={metrics}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
            <LoanEligibilityCard
              metrics={metrics}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SummaryTile
              label="Total Deposits"
              value={metrics.totalDeposits}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
            <SummaryTile
              label="Total Withdrawals"
              value={metrics.totalWithdrawals}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
            <SummaryTile
              label="Share Capital"
              value={metrics.totalShareCapital}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
            <SummaryTile
              label="Outstanding Loans"
              value={metrics.outstandingLoanBalance}
              nativeCurrency={nativeCurrency}
              displayCurrency={displayCurrency}
              convert={convert}
              isLoading={ratesLoading}
              error={ratesError}
            />
          </div>
        </TabsContent>

        {/* Deposits — DepositList has its own Add button */}
        <TabsContent value="deposits">
          <DepositList
            membershipId={membershipId!}
            nativeCurrency={nativeCurrency}
            displayCurrency={displayCurrency}
            convert={convert}
            isLoading={ratesLoading}
            error={ratesError}
          />
        </TabsContent>

        {/* Withdrawals — WithdrawalList manages its own form, needs currentDepositBalance */}
        <TabsContent value="withdrawals">
          <WithdrawalList
            membershipId={membershipId!}
            currentDepositBalance={currentDepositBalance}
            nativeCurrency={nativeCurrency}
            displayCurrency={displayCurrency}
            convert={convert}
            isLoading={ratesLoading}
            error={ratesError}
          />
        </TabsContent>

        {/* Shares — ShareList has its own Add button */}
        <TabsContent value="shares">
          <ShareList
            membershipId={membershipId!}
            nativeCurrency={nativeCurrency}
            displayCurrency={displayCurrency}
            convert={convert}
            isLoading={ratesLoading}
            error={ratesError}
          />
        </TabsContent>

        {/* Loans */}
        <TabsContent value="loans">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setLoanFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Loan
            </Button>
          </div>
          {loans.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No loans recorded yet.
            </p>
          ) : (
            <div className="space-y-4">
              {loans.map((loan) => (
                <div key={loan.id} className="space-y-2">
                  <LoanCard
                    loan={loan}
                    membershipId={membershipId!}
                    nativeCurrency={nativeCurrency}
                    displayCurrency={displayCurrency}
                    convert={convert}
                    isLoading={ratesLoading}
                    error={ratesError}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() =>
                      setExpandedLoanId(expandedLoanId === loan.id ? null : loan.id)
                    }
                  >
                    {expandedLoanId === loan.id ? "Hide" : "Show"} Repayment Schedule
                  </Button>
                  {expandedLoanId === loan.id && (
                    <RepaymentSchedule loanId={loan.id} membershipId={membershipId!} />
                  )}
                </div>
              ))}
            </div>
          )}
          <LoanForm
            open={loanFormOpen}
            onOpenChange={setLoanFormOpen}
            membershipId={membershipId!}
            nativeCurrency={nativeCurrency}
          />
        </TabsContent>

        {/* Rebates — RebateList has its own Add button */}
        <TabsContent value="rebates">
          <RebateList
            membershipId={membershipId!}
            nativeCurrency={nativeCurrency}
            displayCurrency={displayCurrency}
            convert={convert}
            isLoading={ratesLoading}
            error={ratesError}
          />
        </TabsContent>

        {/* Goals — ContributionGoalList has its own Add button */}
        <TabsContent value="goals">
          <ContributionGoalList membershipId={membershipId!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: {
  label: string;
  value: number;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}) {
  let displayValue = value;
  if (!ratesError && !ratesLoading && convert && displayCurrency !== nativeCurrency) {
    try {
      displayValue = convert(value, nativeCurrency, displayCurrency);
    } catch {
      // fall back to raw amount
    }
  }
  const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-lg font-semibold">
        {ratesLoading ? (
          <Skeleton className="h-6 w-24 inline-block" />
        ) : (
          new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: effectiveCurrency,
            minimumFractionDigits: 2,
          }).format(displayValue)
        )}
      </p>
    </div>
  );
}
